import React, { useState } from "react"

import { Form, Formik } from "formik"
import { gql } from "urql"
import { pipe, subscribe } from "wonka"
import * as Yup from "yup"

import { adminClient as client } from "src/urql-client"

import {
  Field,
  FormFailures,
  SubmitButton,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

const ValidationSchema = Yup.object().shape({
  name: Yup.string().required("Please specify a name"),
})

type DuplicateLessonPlanButtonProps = { id: string; buttonClassNames: string }

const DuplicateLessonPlanButton = ({
  id,
  buttonClassNames,
}: DuplicateLessonPlanButtonProps) => {
  const [enqueued, setEnqueued] = useState(false)

  const handleSubmit = (values, actions) => {
    const { unsubscribe } = pipe(
      client.subscription(duplicateSubscription, { id, name: values.name }),
      subscribe(result => {
        if (result.error) {
          setEnqueued(false)
          handleFailure(actions, [{ message: result.error.message }])
          unsubscribe()
          return
        }

        const { status, showPath, failures } = result.data.duplicateLessonPlan

        if (failures?.length) {
          setEnqueued(false)
          handleFailure(actions, failures)
          unsubscribe()
          return
        }

        if (status === "done") {
          unsubscribe()
          window.location.href = showPath
        } else if (status === "enqueued") {
          setEnqueued(true)
        }
      })
    )
  }

  return (
    <ModalWithProvidedBody
      buttonClassName={buttonClassNames}
      buttonText="Duplicate"
      modalClassName="bootstrap-modal"
      modalTitle="Duplicate Lesson Plan"
    >
      {({ closeModal }) => (
        <Formik
          initialValues={{ name: "" }}
          onSubmit={handleSubmit}
          validationSchema={ValidationSchema}
        >
          <Form>
            <div className="modal-body">
              <Field name="name" label="Name" />
              {enqueued && (
                <div className="alert alert-info">
                  The duplication process has begun. Please be patient as this
                  may take a while. Leave this window open and you will be taken
                  to the new lesson plan once it is ready.
                </div>
              )}
            </div>

            <div className="modal-footer flex-col">
              <FormFailures />
              <div className="flex w-full justify-between">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-danger"
                >
                  Cancel
                </button>
                <SubmitButton text="Duplicate" />
              </div>
            </div>
          </Form>
        </Formik>
      )}
    </ModalWithProvidedBody>
  )
}

const duplicateSubscription = gql`
  subscription DuplicateLessonPlan($id: ID!, $name: String!) {
    duplicateLessonPlan(id: $id, name: $name) {
      status
      showPath
      failures {
        message
      }
    }
  }
`

export default DuplicateLessonPlanButton
